/**
 *  ChannelCard script file
 **/
import * as d3 from 'd3'
import filters from '@/filters/customFormatters'
import channelService from '@/api/channelService'
import { CHANNEL_COLORS, CHANNEL_LABELS, CAMPAIGN_STATUSES, CHANNEL_OPTIONS_EXTERIOR } from '@/utils/campaignOptions'
import { CHANNEL_DEFAULT_KPIS } from '../../utils/campaignOptions'
import authService, { USER_ROLES } from '@/api/authService'
import { getChannelBench } from '@/utils/channelUtils'
import i18n from '@/i18n'

export default {
  name: 'ChannelCard',
  mixins: [filters],
  components: {},
  props: {
    type: {
      type: String,
      default: 'channel',
      required: false,
      validator (value) {
        const types = [
          'channel',
          'channel-small',
          'summary',
          'activation'
        ]
        return types.indexOf(value) !== -1
      }
    },
    channel: {
      type: Object,
      required: true
    },
    status: {
      type: String
    },
    link: {
      type: [String, Object],
      required: true,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: true
    },
    helpText: {
      type: String
    },
    closeButton: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      authData: authService.authData,
      kpiSelectedValues: {},
      isDeleting: false
    }
  },
  computed: {
    channelColor () {
      const key = this.channel.type || this.channel.id
      return CHANNEL_COLORS[key]
    },
    channelLabel () {
      const key = this.channel.type || this.channel.id
      return CHANNEL_LABELS[key]
    },
    kpiChartData () {
      return this.channel.kpis.map(kpi => ({ name: kpi.key, values: kpi.data }))
    },
    isExterior () {
      return this.channel.type === CHANNEL_OPTIONS_EXTERIOR.EXTERIOR || this.channel.type === CHANNEL_OPTIONS_EXTERIOR.SIGNALING
    },
    activationKPIs () {
      return this.isExterior
        ? this.exteriorKPIs
        : this.eficiencyKPIs
    },
    eficiencyKPIs () {
      const defaultKpis = [
        { total: 0, key: 'impacts', label: i18n.t('ESTIMATED_IMPRESSIONS'), value: this.channel.impacts || null },
        { total: 0, key: 'coverage', label: i18n.t('ESTIMATED_COVERAGE'), value: this.channel.coverage || null, symbol: '%' }
      ]
      const defaultKpiFilter = CHANNEL_DEFAULT_KPIS[this.channel.type]
      return defaultKpiFilter
        ? defaultKpis.filter(kpi => defaultKpiFilter.indexOf(kpi.key) !== -1)
        : defaultKpis
    },
    exteriorKPIs () {
      const costs = this.channel.costs || {}
      return [
        { total: 0, key: 'month_price', label: i18n.t('MONTH_PRICE'), value: costs.month_price, symbol: '€' },
        { total: 0, key: 'production_cost', label: i18n.t('PRODUCTION_COST'), value: costs.production_cost, symbol: '€' },
        { total: 0, key: 'design_cost', label: i18n.t('DESIGN_COST'), value: costs.design_cost, symbol: '€' }
      ]
    },
    exteriorBudget () {
      const { month_price: mcost, production_cost: pcost, design_cost: dcost } = this.channel.costs || {}
      return mcost + pcost + dcost
    },
    activationBudget () {
      return this.isExterior ? this.exteriorBudget : Number(this.channel.budget)
    },
    ctrBench () {
      return getChannelBench(this.channel)
    },
    isSuperAdmin () {
      return this.authData.user.role === USER_ROLES.ROOT
    },
    isAdmin () {
      return this.authData.user.role === USER_ROLES.ROOT ||
        this.authData.user.role === USER_ROLES.CENTRAL_ADMIN
    },
    canBeDeleted () {
      if (!this.channel.id) return false
      if (this.status === CAMPAIGN_STATUSES.DRAFT) {
        return true
      } else if (this.status === CAMPAIGN_STATUSES.CENTRAL_READY && this.isAdmin) {
        return true
      } else if (this.status === CAMPAIGN_STATUSES.CENTRAL_ACCEPTED && this.isSuperAdmin) {
        return true
      } else {
        return false
      }
    }
  },
  methods: {
    onAreaChartMouseMove (data) {
      const bisectDate = d3.bisector(d => d.date).left
      this.channel.kpis.forEach((kpi, kpiIndex) => {
        const dataIndex = bisectDate(kpi.data, data.date)
        const value = kpi.data[dataIndex].value
        // TODO: change this, store only the index and derive everything else in a computed
        this.$set(this.kpiSelectedValues, kpi.key, value)

        if (data.kpi !== kpi.key) {
          this.$refs.areachart[kpiIndex].drawCrosshair(data.date)
        }
      })
    },
    onAreaChartMouseOut (data) {
      this.kpiSelectedValues = {}
      this.channel.kpis.forEach((kpi, kpiIndex) => {
        if (data !== kpi.key) {
          this.$refs.areachart[kpiIndex].hideCrosshair()
        }
      })
    },
    updateCharts () {
      if (this.$refs.areachart) {
        this.$refs.areachart.forEach(ref => ref.clearChart())
      }
    },
    getSelectedKPIValue ({ key, total }) {
      const value = this.kpiSelectedValues[key]
      return (value || value === 0) ? value : total
    },
    async deleteChannel () {
      if (!this.isDeleting) {
        this.isDeleting = true
        return
      }

      await channelService.deleteChannel({ id: this.channel.id, type: this.channel.type })
      this.$emit('channelsChange')
      this.isDeleting = false
    }
  }
}
